import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

const ExploreContext = createContext(null);

export const useExplore = () => {
  const ctx = useContext(ExploreContext);
  if (!ctx) {
    throw new Error('useExplore must be used within ExploreProvider');
  }
  return ctx;
};

const PILLAR_IDS = ['vision', 'language', 'multimodal', 'systems'];

export function ExploreProvider({ children }) {
  const [explored, setExplored] = useLocalStorage('logitslab-explored', []);
  const [clearedDemos, setClearedDemos] = useLocalStorage('logitslab-cleared-demos', []);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message) => {
    setToast(message);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const markExplored = useCallback((pillarId) => {
    setExplored((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.includes(pillarId)) return list;
      const next = [...list, pillarId];
      if (next.length === PILLAR_IDS.length) {
        setTimeout(() => showToast('Stack unlocked — every layer explored.'), 100);
      }
      return next;
    });
  }, [setExplored, showToast]);

  const markDemoCleared = useCallback((demoId) => {
    setClearedDemos((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.includes(demoId)) return list;
      const next = [...list, demoId];
      setTimeout(() => showToast(`Demo cleared · ${next.length} total`), 100);
      return next;
    });
  }, [setClearedDemos, showToast]);

  const isExplored = useCallback(
    (pillarId) => (Array.isArray(explored) ? explored : []).includes(pillarId),
    [explored]
  );

  const isDemoCleared = useCallback(
    (demoId) => (Array.isArray(clearedDemos) ? clearedDemos : []).includes(demoId),
    [clearedDemos]
  );

  const exploredCount = Array.isArray(explored) ? explored.length : 0;
  const stackUnlocked = exploredCount >= PILLAR_IDS.length;
  const clearedCount = Array.isArray(clearedDemos) ? clearedDemos.length : 0;

  const value = useMemo(
    () => ({
      explored: Array.isArray(explored) ? explored : [],
      exploredCount,
      stackUnlocked,
      markExplored,
      isExplored,
      clearedDemos: Array.isArray(clearedDemos) ? clearedDemos : [],
      clearedCount,
      markDemoCleared,
      isDemoCleared,
      toast,
      dismissToast: () => setToast(null),
      pillarIds: PILLAR_IDS,
    }),
    [
      explored,
      exploredCount,
      stackUnlocked,
      markExplored,
      isExplored,
      clearedDemos,
      clearedCount,
      markDemoCleared,
      isDemoCleared,
      toast,
    ]
  );

  return (
    <ExploreContext.Provider value={value}>
      {children}
      {toast && (
        <div className="explore-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </ExploreContext.Provider>
  );
}
